import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";

const AUTH_URL = process.env.REACT_APP_AUTH_URL || "/auth";
const TOKEN_KEY = "hyveai_token";

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [clients, setClients] = useState([]);
  // Aggregated facility-level roles from user_service_access.facility_roles.
  // Populated from /auth/me response field `facility_roles`.
  // Used to gate portal sections beyond the coarse global role check.
  const [facilityRoles, setFacilityRoles] = useState([]);
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  const clearAuth = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setClients([]);
    setFacilityRoles([]);
  }, []);

  const verify = useCallback(async () => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(`${AUTH_URL}/me`, {
        headers: { Authorization: `Bearer ${stored}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        setClients(data.clients || []);
        // Backend returns aggregated facility_roles at top level or on user object
        setFacilityRoles(
          data.facility_roles || data.user?.facility_roles || [],
        );
        setToken(stored);
      } else {
        clearAuth();
      }
    } catch {
      clearAuth();
    }
    setLoading(false);
  }, [clearAuth]);

  useEffect(() => {
    verify();
  }, [verify]);

  const login = async (email, password) => {
    const res = await fetch(`${AUTH_URL}/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.error || "Login failed");
    }

    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    setClients(data.clients || []);
    setFacilityRoles(data.facility_roles || data.user?.facility_roles || []);
    return data;
  };

  const logout = async () => {
    if (token) {
      try {
        await fetch(`${AUTH_URL}/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Ignore — clear local state regardless
      }
    }
    clearAuth();
  };

  const changePassword = async (currentPassword, newPassword) => {
    const res = await fetch(`${AUTH_URL}/change-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Password change failed");
    return data;
  };

  const setPasswordForUser = async (userId, password) => {
    const res = await fetch(`${AUTH_URL}/set-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ user_id: userId, password }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Set password failed");
    return data;
  };

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager" || isAdmin;
  const isAuthenticated = !!user && !!token;

  const hasFacilityRole = (role) => facilityRoles.includes(role);

  const canAccessSection = (sectionId) => {
    if (isAdmin) return true;
    if (sectionId === "my-portal") return true;
    const managerSections = ["client-management", "data-mapping", "billing"];
    if (isManager) return managerSections.includes(sectionId);
    return false;
  };

  const getClientIds = () => clients.map((c) => c.id);

  return (
    <AuthContext.Provider
      value={{
        user,
        clients,
        facilityRoles,
        hasFacilityRole,
        token,
        loading,
        isAuthenticated,
        isAdmin,
        isManager,
        login,
        logout,
        changePassword,
        setPasswordForUser,
        canAccessSection,
        getClientIds,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
